import { useEffect, useState } from "react";
import { Box, Divider, List, Stack, Typography } from "@mui/joy";
import dayjs from "dayjs";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import MeditationRecordListItem from "./MeditationRecordListItem";
import AddNewMeditationRecordButton from "./AddNewMeditationRecordButton";
import { MeditationRecordType, MeditationGroupType } from "api/meditationRecordApi";
import OpenStatisticsButton from "./OpenStatisticsButton";
import useGetMeditationRecordVersion from "hooks/useGetMeditationRecordVersion";
import TabButtonList from "../../common/TabButtonList";
import { removeDuplicate } from "logic/logics_common";
import useIsInappWebview from "hooks/useIsInappWebview";

export default function MeditationRecordList({
  recordList,
  currentRecord,
  setCurrentRecord,
  isStatisticsOpen,
  openStatistics,
}: {
  recordList?: MeditationRecordType[];
  currentRecord?: MeditationRecordType;
  setCurrentRecord: React.Dispatch<
    React.SetStateAction<MeditationRecordType | undefined>
  >;
  isStatisticsOpen: boolean;
  openStatistics: () => void;
}) {
  const user = useRecoilValue(userAtom);
  const isCoach = user?.role !== "patient";
  // const { isTaskContainerSmall } = useRecoilValue(layoutAtom);
  const isInappWebview = useIsInappWebview();
  const isTaskContainerSmall = isInappWebview;
  const [listData, setListData] = useState<MeditationRecordType[] | undefined>(
    recordList
  );

  const { isRoutineAvailable, isCopingAvailable } =
    useGetMeditationRecordVersion();

  const [existTabList] = useState<MeditationGroupType[]>(["routine", "coping"]);
  const [selectedTab, setSelectedTab] = useState<MeditationGroupType>(
    isRoutineAvailable
      ? "routine"
      : isCopingAvailable
        ? "coping"
        : "routine"
  );

  useEffect(() => {
    if (currentRecord?.type) {
      setSelectedTab(currentRecord.type);
    }
  }, [currentRecord]);

  const [currentTabListData, setCurrentTabListData] = useState<
    MeditationRecordType[] | undefined
  >(recordList?.filter((element) => element.type === selectedTab));

  useEffect(() => {
    setListData(recordList);
    const existTabList = removeDuplicate(recordList?.map((each) => each.type));
    // setExistTabList(existTabList as MeditationType[]);
    if (existTabList.length === 1) {
      setSelectedTab(existTabList[0] as MeditationGroupType);
    }
  }, [recordList]);

  useEffect(() => {
    setCurrentTabListData(
      listData?.filter(
        (element) => !element.type || element.type === selectedTab
      )
    );
  }, [selectedTab, listData]);

  return (
    <Stack
      alignItems="center"
      sx={{
        flexShrink: 0,
        width: isTaskContainerSmall ? "100%" : "320px",
        height: isTaskContainerSmall ? "fit-content" : "100%",
        // overflow: "scroll",
        transition: "transform 0.4s, width 0.4s",
        scrollPaddingBlockStart: "20px",
        boxSizing: "border-box",
        ...(isTaskContainerSmall && { pb: "20px" }),
      }}
      className="scrollbar-hide"
    >
      <Stack
        direction={isTaskContainerSmall ? "row" : "column"}
        justifyContent={"center"}
        spacing={isTaskContainerSmall ? "8px" : "16px"}
        sx={{
          width: "100%",
          // backgroundColor: "background.level1",

          ...(isTaskContainerSmall
            ? {
                px: "20px",
                height: "36px",
                mb: "16px",
              }
            : {
                flexShrink: 0,
                zIndex: 10,
                position: "sticky",
                py: "8px",
                top: 0,
                px: "20px",
              }),
        }}
      >
        <Stack
          spacing={1}
          direction={isTaskContainerSmall ? "row-reverse" : "column"}
          sx={{
            flexShrink: 0,
            width: "100%",
            ...(isTaskContainerSmall && {
              height: "100%",
              alignItems: "center",
              justifyContent: "center",
            }),
          }}
        >
          {existTabList && existTabList.length > 1 && (
            <TabButtonList
              selectedTab={selectedTab}
              data={[
                {
                  id: "routine",
                  label: "연습하기",
                  onClick: () => {
                    setSelectedTab("routine");
                    // 탭 전환 시 현재 선택을 해당 탭의 첫 항목으로 동기화
                    if (currentRecord?.type !== "routine") {
                      const targetList = (listData || []).filter(
                        (element) => !element.type || element.type === "routine"
                      );
                      if (targetList.length > 0) {
                        setCurrentRecord(targetList[0]);
                      }
                    }
                  },
                },
                {
                  id: "coping",
                  label: "다스리기",
                  onClick: () => {
                    setSelectedTab("coping");
                    // 탭 전환 시 현재 선택을 해당 탭의 첫 항목으로 동기화
                    if (currentRecord?.type !== "coping") {
                      const targetList = (listData || []).filter(
                        (element) => !element.type || element.type === "coping"
                      );
                      if (targetList.length > 0) {
                        setCurrentRecord(targetList[0]);
                      }
                    }
                  },
                },
              ]}
            />
          )}
          <AddNewMeditationRecordButton
            disabled={
              isCoach ||
              (selectedTab === "coping" && !isCopingAvailable) ||
              ((listData || []).length === 1 &&
                !listData?.every((element) => element.lastDoneAt))
            }
            groupType={selectedTab}
          />
        </Stack>
      </Stack>

      <List
        orientation={isTaskContainerSmall ? "horizontal" : "vertical"}
        className="scrollbar-hide"
        sx={{
          py: 0,
          px: "20px",
          overflow: "scroll",
          width: "100%",
          "--ListItem-paddingY": 0,
          "--ListItem-paddingX": 0,
          "--ListDivider-gap": 0,
          ...(isTaskContainerSmall && { gap: "4px" }),
        }}
      >
        {isRoutineAvailable && selectedTab === "routine" && (
          <Box
            sx={{ ...(isTaskContainerSmall ? { mx: "12px" } : { mb: "12px" }) }}
          >
            <OpenStatisticsButton
              isOpen={isStatisticsOpen}
              open={openStatistics}
            />
          </Box>
        )}

        {currentTabListData && currentTabListData.length > 0 ? (
          currentTabListData.map((record, index) => {
            return (
              <MeditationRecordListItem
                key={record.meditationRecordKey}
                width={isTaskContainerSmall ? "58px" : "100%"}
                direction={isTaskContainerSmall ? "row" : "column"}
                isFirstRecordOfMonth={
                  index === 0 ||
                  (index - 1 >= 0 &&
                    dayjs(currentTabListData[index - 1].date).get("month") !==
                      dayjs(currentTabListData[index].date).get("month"))
                }
                record={record}
                isSelected={
                  currentRecord?.meditationRecordKey ===
                  record.meditationRecordKey
                }
                setCurrentRecord={setCurrentRecord}
                shouldBeDone={!isCoach && record.shouldBeDone}
                durationMinutes={record.durationMinutes}
              />
            );
          })
        ) : (
          <Stack alignSelf={"center"} sx={{ width: "100%" }}>
            <Typography
              sx={{
                width: "100%",
                fontSize: "14px",
                opacity: 0.6,
                textAlign: "center",
              }}
            >
              {selectedTab === "routine"
                ? isRoutineAvailable
                  ? "진행한 훈련이 없어요."
                  : "아직 배우지 않았어요. 다스리기 훈련에 집중해주세요."
                : isCopingAvailable
                  ? "진행한 훈련이 없어요."
                  : "아직 배우지 않았어요. 연습하기 훈련에 집중해주세요."}
            </Typography>
          </Stack>
        )}
      </List>
    </Stack>
  );
}
